import React, { useState, useEffect, useContext } from "react";
import { FaTimes } from "react-icons/fa";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { UserContext } from "../context/UserContext";

const CharityReport = ({ onClose }) => {
  const [reports, setReports] = useState([]);
  const { emailId, isLoggedIn } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedMonths, setExpandedMonths] = useState(new Set());

  useEffect(() => {
    if (!isLoggedIn || emailId !== "info@urcommunity.com.au") return;

    const fetchReports = async () => {
      setIsLoading(true);
      try {
        const response = await fetch("/api/charity-reports");
        if (!response.ok) throw new Error("Failed to fetch charity reports");
        const data = await response.json();
        setReports(data);
        
        // Set current month as expanded by default
        const currentDate = new Date();
        const currentMonthYear = `${currentDate.toLocaleString('default', { month: 'short' })}-${currentDate.getFullYear()}`;
        setExpandedMonths(new Set([currentMonthYear]));
      } catch (error) {
        console.error("Error fetching charity reports:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReports();
  }, [emailId, isLoggedIn]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const toggleMonth = (monthYear) => {
    setExpandedMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(monthYear)) {
        newSet.delete(monthYear);
      } else {
        newSet.add(monthYear);
      }
      return newSet;
    });
  };

  const groupDonationsByMonthAndCharity = (donations) => {
    return donations.reduce((acc, donation) => {
      const date = new Date(donation.timestamp);
      const monthYear = `${date.toLocaleString('default', { month: 'short' })}-${date.getFullYear()}`;
      
      if (!acc[monthYear]) {
        acc[monthYear] = {};
      }
      
      if (!acc[monthYear][donation.charity_name]) {
        acc[monthYear][donation.charity_name] = {
          totalAmount: 0,
          charityCreator: donation.donor,
          donationCount: 0
        };
      }
      
      acc[monthYear][donation.charity_name].totalAmount += parseFloat(donation.amount);
      acc[monthYear][donation.charity_name].donationCount += 1;
      
      return acc;
    }, {});
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 999,
    }}>
      <div style={{
        backgroundColor: "#fff",
        borderRadius: "10px",
        padding: "20px",
        width: "95%",
        maxWidth: "800px",
        maxHeight: "80vh",
        overflowY: "auto",
        boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
          <h3 style={{ margin: 0 }}>Charity Report</h3>
          <FaTimes onClick={onClose} style={{ cursor: "pointer", fontSize: "1.5rem" }} />
        </div>

        {isLoading ? (
          <p style={{ textAlign: "center" }}>Loading charity reports...</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            {Object.entries(groupDonationsByMonthAndCharity(reports))
              .sort((a, b) => new Date(b[0]) - new Date(a[0]))
              .map(([monthYear, charities]) => (
                <div key={monthYear} style={{ marginBottom: "15px" }}>
                  <div 
                    onClick={() => toggleMonth(monthYear)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px",
                      backgroundColor: "#f8f9fa",
                      cursor: "pointer",
                      borderRadius: "5px",
                    }}
                  >
                    <h4 style={{ margin: 0 }}>{monthYear}</h4>
                    {expandedMonths.has(monthYear) ? <FaChevronUp color="red" /> : <FaChevronDown color="red" />}
                  </div>
                  
                  {expandedMonths.has(monthYear) && (
                    <div style={{ marginTop: "10px" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ backgroundColor: "#007bff", color: "#fff" }}>
                            <th style={{ padding: "10px", textAlign: "left" }}>Charity Name</th>
                            <th style={{ padding: "10px", textAlign: "left" }}>Charity Creator</th>
                            <th style={{ padding: "10px", textAlign: "right" }}>Total Amount by month</th>
                            <th style={{ padding: "10px", textAlign: "center" }}>Number of Donations</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(charities).map(([charityName, data]) => (
                            <tr key={charityName} style={{ borderBottom: "1px solid #dee2e6" }}>
                              <td style={{ padding: "10px" }}>{charityName}</td>
                              <td style={{ padding: "10px" }}>{data.charityCreator}</td>
                              <td style={{ padding: "10px", textAlign: "right" }}>
                                {formatCurrency(data.totalAmount)}
                              </td>
                              <td style={{ padding: "10px", textAlign: "center" }}>
                                {data.donationCount}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CharityReport;
